import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Checkbox, FormControl, FormControlLabel } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import { makeStyles } from '@material-ui/styles';
import PropTypes from 'prop-types';
import { NOT_APPLICABLE } from 'common/constants/general';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import withTooltip from 'components/WithTooltip';
import { formatNumbers } from 'utillities';

const useStyles = makeStyles({
  root: {
    width: '100%',
    '&& .MuiCheckbox-root': {
      padding: '0px 16px 0px 3px',
    },
  },
  bold: {
    fontWeight: 'bold',
  },
  strike: {
    textDecoration: 'line-through',
    color: grey,
  },
});

const Strike = ({ value, enabled, isInvalidExpr, classes }) => {
  const valueLabel = isInvalidExpr ? NOT_APPLICABLE : value;
  if (!enabled) {
    return <span className={classes.strike}>{valueLabel}</span>;
  }
  return <span>{valueLabel}</span>;
};

const NumberMultipleCheckbox = ({ cell, selected, value }) => {
  const {
    enabled: cellEnabled,
    ignoreExpr: cellIgnoreExpr,
    expr: cellExpr,
    value: cellValue,
    format: cellFormat,
    suffix: cellSuffix,
    allowNegativeValue: cellAllowNegativeValue,
    prefix: cellPrefix,
    currency: cellCurrency,
    currencyCode: cellCurrencyCode,
    blankIfNull: cellBlankIfNull,
    staticCurrency: cellStaticCurrency,
    readOnly: cellReadOnly,
  } = cell;

  const classes = useStyles();
  const { onCellsChanged } = useContext(FeaturedSpreadsheetContext);
  const [enabled, setEnabled] = useState(cellEnabled);
  const [disableChangeState, setDisableChangeState] = useState(false);

  const isInvalidExpr = cellIgnoreExpr === true;

  const changeCell = useCallback(
    enable => {
      cell.setProps({ enabled: enable });
      setEnabled(enable);
      onCellsChanged([{ cell, value: cellExpr }]);
    },
    [cell, cellExpr, onCellsChanged]
  );

  useEffect(() => {
    if (isInvalidExpr) {
      changeCell(false);
    } else {
      setEnabled(cellEnabled);
    }

    setDisableChangeState(isInvalidExpr);
  }, [cellEnabled, changeCell, isInvalidExpr]);

  const keyUpDownHandler = useCallback(
    event => {
      const { code, type } = event;
      if (selected && code === 'Space') {
        event.preventDefault();

        if (type === 'keyup') changeCell(!cellEnabled);
      }
    },
    [cellEnabled, changeCell, selected]
  );

  useEffect(() => {
    document.addEventListener('keyup', keyUpDownHandler);
    document.addEventListener('keydown', keyUpDownHandler);
    return () => {
      document.removeEventListener('keyup', keyUpDownHandler);
      document.removeEventListener('keydown', keyUpDownHandler);
    };
  }, [keyUpDownHandler]);

  const handleChange = useCallback(
    ({ target: { checked } }) => {
      if (cellReadOnly) return;
      changeCell(checked);
    },
    [changeCell, cellReadOnly]
  );

  const CellValueFormatted = useMemo(
    () =>
      formatNumbers({
        format: cellFormat,
        prefix: cellPrefix,
        suffix: cellSuffix,
        allowNegativeValue: cellAllowNegativeValue,
        currency: cellCurrency,
        currencyCode: cellCurrencyCode,
        blankIfNull: cellBlankIfNull,
        staticCurrency: cellStaticCurrency,
        value: cellValue,
      }),

    [
      cellAllowNegativeValue,
      cellBlankIfNull,
      cellCurrency,
      cellCurrencyCode,
      cellFormat,
      cellPrefix,
      cellStaticCurrency,
      cellSuffix,
      cellValue,
    ]
  );

  const numberDisplay = useMemo(
    () => <Strike value={CellValueFormatted} enabled={enabled} isInValidExpr={isInvalidExpr} classes={classes} />,
    [CellValueFormatted, isInvalidExpr, enabled, classes]
  );

  return (
    <div>
      {selected ? (
        <FormControl className={classes.root}>
          <FormControlLabel
            className={classes.root}
            control={<Checkbox disabled={disableChangeState} checked={enabled} onChange={handleChange} />}
            label={numberDisplay}
            labelPlacement="start"
          />
        </FormControl>
      ) : (
        <span className="unselected-checkbox-cell">{numberDisplay}</span>
      )}
    </div>
  );
};

NumberMultipleCheckbox.propTypes = {
  cell: PropTypes.shape({
    alias: PropTypes.string,
    allowNegativeValue: PropTypes.bool,
    className: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({}), PropTypes.instanceOf(undefined)]),
    code: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.instanceOf(undefined)]),
    columnId: PropTypes.string,
    columnLegend: PropTypes.string,
    columnOrder: PropTypes.oneOfType([PropTypes.number, PropTypes.instanceOf(undefined)]),
    component: PropTypes.shape({}),
    defaultValue: PropTypes.number,
    dependencies: PropTypes.instanceOf(Set),
    enabled: PropTypes.bool,
    evaluated: PropTypes.bool,
    expr: PropTypes.string,
    forceComponent: PropTypes.bool,
    format: PropTypes.shape({}),
    gridType: PropTypes.string,
    hidden: PropTypes.bool,
    ignoreExpr: PropTypes.bool,
    ignoreRowCopy: PropTypes.bool,
    invalidExpr: PropTypes.bool,
    isEditableTitleCell: PropTypes.bool,
    isExpr: PropTypes.bool,
    isGptRow: PropTypes.bool,
    isRequired: PropTypes.bool,
    key: PropTypes.string,
    linkedCells: PropTypes.instanceOf(Set),
    parentColumn: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(undefined)]),
    providers: PropTypes.instanceOf(Set),
    readOnly: PropTypes.bool,
    revenue: PropTypes.string,
    rowNumber: PropTypes.number,
    row_ref: PropTypes.string,
    sheet: PropTypes.shape({}),
    suffix: PropTypes.string,
    symbols: PropTypes.instanceOf(Set),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    valueViewer: PropTypes.func,

    // Unknown fields
    prefix: PropTypes.string,
    currency: PropTypes.string,
    currencyCode: PropTypes.string,
    blankIfNull: PropTypes.bool,
    staticCurrency: PropTypes.string,
  }).isRequired,
  selected: PropTypes.bool.isRequired,
};

Strike.propTypes = {
  value: PropTypes.string,
  enabled: PropTypes.bool,
  isInvalidExpr: PropTypes.bool,
  classes: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.instanceOf(undefined)]),
};

export default withTooltip(NumberMultipleCheckbox);
